<template>
	<div class="row centered">
		<AppTable ref="transfers-table"
			v-if="userStore.currBusinessId"
			:fetch-function="administrationStore.getFilteredStripeBalance"
			:filters="filters"
			:table-headings="tableHeadings"
			:title="getString('transfers_history')"
			:total-items="administrationStore.businessTransfersTotal || 0"
			:items="administrationStore.businessTransfers || []"
			initial-sort-field="date"
			@failed-fetch="details=>{$emit('failed-fetch', details)}"
			allow-download
			@export="exportTransfers">

			<template #reconcile="{item}">
				<div class="row centered">
					<AppTableButtonGroup :buttons="buttonsReportPayout" @picked-report-payout="openReportPayout(item)"/>
				</div>
			</template>

		</AppTable>
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="administrationStore.activeTransfer !== null" @toggling-popup="closeBottomSheet">
			<div class="report-container" v-if="administrationStore.activeTransfer !== null">
				<div class="warning-limit" v-if="limitReached">{{ getString('limit_reached_warning') }}</div>
				<AppTable ref="reconcile-transfers-table"
						  v-if="userStore.currBusinessId"
						  :table-headings="tableHeadingTransactions"
						  :title="reportPayoutTitle"
						  :total-items="transactions.length || 0"
						  :items="currentShownTransactions || []"
						  @changed-page="v=>currentPageTransactions = v"
						  @changed-page-size="v=>pageSizeTransactions = v"
						  allow-download
						  @export="exportTransactions" />
			</div>
		</AppBottomSheet>
	</div>
</template>

<script>
import AppTable from "@/shared/components/AppTable.vue";
import {useUserStore, useAdministrationStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore, useScriptStore, useNotificationStore} from "@/shared/stores";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {formatDateMMDDHHmm, formatDateTimeWithSeconds} from "@/shared/helpers/dates";
import {exportToFile} from "@/shared/helpers/excel";
import AppTableButtonGroup from "@/shared/components/AppTableButtonGroup.vue";

export default {
	setup() {
		const userStore = useUserStore();
		const administrationStore = useAdministrationStore();
		const languageStore = useLanguageStore();
		const currencyStore = useCurrencyStore();
		const scriptStore = useScriptStore();
		const notificationStore = useNotificationStore();
		return {
			userStore,
			administrationStore,
			getString: languageStore.getString,
			languageStore,
			currencyStore,
			scriptStore,
			showNotification: notificationStore.showNotification,
		};
	},
	name: "TransfersTable",
	emits: ["failed-fetch"],
	components: {AppTableButtonGroup, ModalHeader, AppBottomSheet, AppButtonIcon, AppTable},
	mixins: [WindowWidth],
	data() {
		return {
			filters: [
				{
					type: "date",
					key: 'date',
					tableName: 'transfers',
					text: this.getString('date'),
					finalValue: '',
					active: false
				},
				{
					type: "price",
					key: "amount",
					text: this.getString('amount'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "checklist",
					key: "status",
					text: this.getString('status'),
					values: [this.getString('completed'), this.getString('pending'), this.getString('in_transit'), this.getString('canceled'), this.getString('failed'), this.getString('available')],
					keysArray: ['paid', 'pending', 'in_transit', 'canceled', 'failed', 'merged', 'available'],
					active: false,
					selectedIndexes: [],
					finalValue: '',
				},
				{
					type: "text",
					key: "payout_id",
					text: this.getString('id'),
					placeholder: this.getString('id'),
					value: "",
					active: false,
					finalValue: '',
				},
			],
			transactions: [],
			limitReached: false,
			hasMore: false,
			currentPageTransactions: 1,
			pageSizeTransactions: 10,
		}
	},
	computed: {
		bottomSheetWidth() {
			if (this.windowWidth < 550) {
				return "100%";
			} else if (this.windowWidth < 768) {
				return "550px";
			} else if (this.windowWidth < 1080) {
				return "720px";
			} else if (this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
		currentShownTransactions() {
			return this.transactions.slice(
				(this.currentPageTransactions - 1) * this.pageSizeTransactions,
				this.currentPageTransactions * this.pageSizeTransactions
			);
		},
		buttonsReportPayout() {
			return [
				{
					iconName: "icon-wire-transfer-blue",
					emitName: "picked-report-payout",
				},
			];
		},
		allTransfersNativeCurrency() {
			return !this.administrationStore?.businessTransfers?.length || this.administrationStore.businessTransfers.every(t => t?.currency === this.currencyStore.currCurrency.code.toLowerCase());
		},
		allReconcileTransactionsNativeCurrency() {
			return !this.transactions?.length || this.transactions.every(t => t?.currency === this.currencyStore.currCurrency.code.toLowerCase());
		},
		tableHeadings() {
			let baseHeadings = [
				{
					text: this.getString('date'),
					cols: 1,
					sortable: true,
					fieldName: 'date',
				},
				{
					text: this.allTransfersNativeCurrency ? this.getString('amount_x', [this.currencyStore.currCurrency.symbol]) : this.getString('amount'),
					cols: 1,
					sortable: true,
					fieldName: 'amount',
					formatter: v => {
						return this.currencyStore.readableCurrencyValue(v, true, false);
					}
				}
			];
			if(!this.allTransfersNativeCurrency) {
				baseHeadings.push({
					text: this.getString('currency'),
					cols: 1,
					fieldName: 'currency',
					formatter: code => {
						return this.currencyStore.getCurrencyFromCode(code).code;
					}
				});
			}
			baseHeadings.push(
				{
					text: this.getString('status'),
					fieldName: 'status',
					cols: 1,
				},
				{
					text: this.getString('reconcile'),
					cols: 1,
					fieldName: 'reconcile',
					slotName: 'reconcile',
				},
				{
					text: this.getString('id'),
					cols: 2,
					fieldName: 'payout_id',
				}
			)
			return baseHeadings;
		},
		tableHeadingTransactions() {
			let baseHeadings = [
				{
					text: this.getString('date'),
					cols: 1,
					fieldName: 'date',
				},
				{
					text: this.getString('type'),
					cols: 1,
					fieldName: 'type',
				},
				{
					text: this.allReconcileTransactionsNativeCurrency ? this.getString('gross_x', [this.currencyStore.currCurrency.symbol]) : this.getString('gross'),
					cols: 1,
					fieldName: 'gross',
				},
				{
					text: this.allReconcileTransactionsNativeCurrency ? this.getString('net_x', [this.currencyStore.currCurrency.symbol]) : this.getString('net'),
					cols: 1,
					fieldName: 'net',
				},
				{
					text: this.allReconcileTransactionsNativeCurrency ? this.getString('fee_x', [this.currencyStore.currCurrency.symbol]) : this.getString('fee'),
					cols: 1,
					fieldName: 'fee',
				},
			];

			if(!this.allReconcileTransactionsNativeCurrency) {
				baseHeadings.push({
					text: this.getString('currency'),
					cols: 1,
					fieldName: 'currency',
					formatter: code => {
						return this.currencyStore.getCurrencyFromCode(code).code;
					}
				});
			}
			return baseHeadings;
		},
		reportPayoutTitle() {
			let activeTransfer = this.administrationStore.activeTransfer;
			let transferValue = activeTransfer ? this.currencyStore.readableCurrencyValue(activeTransfer.amount, true, true, false, activeTransfer.currency) : '';
			return this.getString('report_payout', [transferValue])
		}
	},
	methods: {
		exportTransactions(expExtension) {
			let nativeCurrency = this.allReconcileTransactionsNativeCurrency;
			let sym = this.currencyStore.currCurrency.symbol;
			let columns = [
				{ header: this.getString('date'), key: 'date' },
				{ header: this.getString('type'), key: 'type' },
				{ header: nativeCurrency ? this.getString('gross_x', [sym]) : this.getString('gross'), key: 'gross_value', type: 'number', numberFormat: '#,##0.00' },
				{ header: nativeCurrency ? this.getString('net_x', [sym]) : this.getString('net'), key: 'net_value', type: 'number', numberFormat: '#,##0.00' },
				{ header: nativeCurrency ? this.getString('fee_x', [sym]) : this.getString('fee'), key: 'fee_value', type: 'number', numberFormat: '#,##0.00' },
			];
			if (!nativeCurrency) {
				columns.push({ header: this.getString('currency'), value: p => this.currencyStore.getCurrencyFromCode(p.currency).code });
			}
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let fileName = businessNameNorm+"_"+this.getString("wire").toLowerCase()+"_"+this.administrationStore.activeTransfer.payout_id;
			exportToFile({
				columns,
				rows: this.transactions,
				fileName,
				sheetName: this.getString("wire"),
				format: expExtension,
				onEmpty: () => this.showNotification('table-no-data'),
			});
		},
		closeBottomSheet(isOpen) {
			if(!isOpen) {
				this.administrationStore.activeTransfer = null;
				this.limitReached = false;
				this.transactions = [];
				this.currentPageTransactions = 1;
				this.pageSizeTransactions = 10;
			}
		},
		openReportPayout(item) {

			this.administrationStore.activeTransfer = item;
			this.administrationStore.getReconcileTransactionsTransfer(r => {
				this.transactions = r.transactions.filter(t => !(t.type === "payout" && t.source === item.payout_id));
				this.limitReached = !!r.limit_reached;
				this.transactions.forEach(t => {
					if(t.type === "payment") {
						t.type = this.getString('payment');
					} else if(t.type === "payout") {
						t.type = this.getString('wire');
					}
					t.net_value = t.net ? this.currencyStore.dbToActualValue(t.net) : 0;
					t.net = t.net ? this.currencyStore.readableCurrencyValue(t.net, true, false) : '-';
					t.fee_value = t.fee ? this.currencyStore.dbToActualValue(t.fee) : 0;
					t.fee = t.fee ? this.currencyStore.readableCurrencyValue(t.fee, true, false) : '-';
					t.gross_value = t.amount ? this.currencyStore.dbToActualValue(t.amount) : 0;
					t.gross = t.amount ? this.currencyStore.readableCurrencyValue(t.amount, true, false) : '-';
					t.date = t.created ? formatDateMMDDHHmm(new Date(t.created*1000)) : '-';
				})
			})
		},
		exportTransfers(expExtension) {
			let nativeCurrency = this.allTransfersNativeCurrency;
			let sym = this.currencyStore.currCurrency.symbol;
			let columns = [
				{ header: this.getString('date'), value: t => formatDateTimeWithSeconds(new Date(t.date), false) },
				{ header: nativeCurrency ? this.getString('amount_x', [sym]) : this.getString('amount'), value: t => this.currencyStore.dbToActualValue(t.amount), type: 'number', numberFormat: '#,##0.00' },
			];
			if (!nativeCurrency) {
				columns.push({ header: this.getString('currency'), value: t => this.currencyStore.getCurrencyFromCode(t.currency).code });
			}
			columns.push(
				{ header: this.getString('status'), key: 'status' },
				{ header: this.getString('id'), key: 'payout_id' },
			);
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let today = new Date().toLocaleString();
			let fileName = businessNameNorm+"_"+this.getString('transfers_history').toLowerCase()+"_"+today.replace(", ", "_");
			exportToFile({
				columns,
				rows: this.administrationStore.businessTransfers,
				fileName,
				sheetName: this.getString("transfers_history"),
				format: expExtension,
				onEmpty: () => this.showNotification('table-no-data'),
			});
		}
	},
	watch: {
		'userStore.currBusinessId': {
			handler(newVal, oldVal) {
				if(newVal && newVal !== oldVal) {
					if(this.$refs['transfers-table']) {
						this.$refs['transfers-table'].fetchData();
					}
				}
			},
		},
	}
}
</script>

<style lang="scss" scoped>
.report-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 100%;

	.warning-limit {
		margin-top: 10px;
		margin-bottom: 10px;
		font-weight: 700;
		color: var(--color-danger);
	}
}
</style>

